import { usePriceFormat } from '@/hooks/usePriceFormat'
import { Button, FormLabel, HStack, Input, Select, Stack } from '@chakra-ui/react'
import type { AdditionMedical, OfferProperty, OfferPropertyAddition, PackageItemModel } from 'BrokerTsLib/src/dtos'
import { Insurer } from 'BrokerTsLib/src/enums'
import type { UseFieldArrayRemove } from 'BrokerTsLib/src/rhf'
import { useFieldArray, useFormContext } from 'BrokerTsLib/src/rhf'
import { useEffect } from 'react'
import { Add } from '../../../../../icons'

type MedicalProps = {
  offer: OfferProperty
  addon: PackageItemModel
}

const getDefaultItem = (addon: PackageItemModel) => {
  const defaultVariant = addon.variants.find(x => x.isDefault) ?? addon.variants[0]

  return {
    packageItemId: addon.packageItemId,
    variantId: defaultVariant?.id,
    limit: defaultVariant?.defaultLimit?.toString(),
    riskId: addon.riskId,
  } as AdditionMedical
}


function Medical({
  offer,
  addon
}: MedicalProps) {

  const {
    control,
    setValue
  } = useFormContext<OfferPropertyAddition>()

  const name = 'medicals'

  const { append, fields, remove } = useFieldArray({
    control: control,
    name: name
  })

  // vychozi nastaveni hodnot na OfferPropertyAddition z OfferPart
  useEffect(() => {
    fields.forEach((item, index) => {
      const part = offer.parts.find(x => x.partId == item.offerPartId)
      if (!part) return

      setValue(`${name}.${index}.riskId`, part.packageItem.riskId)
      setValue(`${name}.${index}.limit`, part.limit?.toString())
      setValue(`${name}.${index}.variantId`, part.packageItemVariantId)
      setValue(`${name}.${index}.offerPartId`, part.partId)
      setValue(`${name}.${index}.packageItemId`, part.packageItem.packageItemId)
    })

    if (fields.length == 0)
      append(getDefaultItem(addon))
  }, [])

  return (
    <HStack gap={8}>
      {fields?.map((item, index) => {
        return <MedicalItem
          key={`${item.offerPartId}_${index}`}
          addon={addon}
          index={index}
          item={item}
          remove={remove}
          offer={offer}
        />
      })}

      {offer.insurerId == Insurer.Direct && fields.length < 4 &&
                <Stack>
                  <Button
                    variant='outline'
                    onClick={() => append(getDefaultItem(addon))}
                  >
                    <Add color="secondary.500" />
                    Další
                  </Button>
                </Stack>
      }
    </HStack >
  )
}

export default Medical

type MedicalItemProps = {
  item: AdditionMedical,
  index: number,
  addon: PackageItemModel,
  remove: UseFieldArrayRemove
  offer: OfferProperty
}

const MedicalItem = ({
  item,
  index,
  addon,
  remove,
  offer
}: MedicalItemProps) => {

  const name = 'medicals'
  const { priceFormat } = usePriceFormat()

  const {
    register,
    setValue,
    watch
  } = useFormContext<OfferPropertyAddition>()

  const handleChangeVariant = (limit: number, variantId?: number) => {
    setValue(`${name}.${index}.limit`, limit?.toString())
    setValue(`${name}.${index}.variantId`, variantId)
  }

  return (
    <Stack key={`${item.offerPartId}_${index}`} alignSelf='start' minW='120px'>
      <FormLabel alignSelf='center'>{index + 1}. pomůcka</FormLabel>

      <Select
        size='sm'
        sx={{ width: '100%' }}
        {...register(`${name}.${index}.limit`)}
        onChange={(e) => {
          const dataset = e.target.options[e.target.selectedIndex].dataset
          handleChangeVariant(parseInt(e.target.value), parseInt(dataset.id!))
        }}
      >
        {addon.variants.map(x => (
          <option key={`${x.id}_${x.defaultLimit}`} value={x.defaultLimit?.toString()} data-id={x.id}>
            {priceFormat(x.defaultLimit)} {x.isDefault ? '(výchozí)' : ''}
          </option>
        ))}
      </Select>

      {offer.insurerId == Insurer.Direct &&
                <Input
                  size='sm'
                  placeholder='Typ'
                  {...register(`${name}.${index}.type`)} />
      }

      <Input
        size='sm'
        placeholder='Popis'
        {...register(`${name}.${index}.description`)} />

      <Button
        size='sm'
        colorScheme='secondary'
        variant='ghost'
        onClick={() => remove(index)}
      >
        Odebrat
      </Button>
    </Stack>
  )
}
